import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  HostBinding,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  Renderer2
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import {
  ChatApiService,
  ChatFigure,
  ChatStreamEvent,
} from '../../shared-services/chat-api.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DynamicEditTableComponent } from '../../chart-components/dynamic-edit-table/dynamic-edit-table.component';
import { StreamingTextComponent } from '../streaming-text/streaming-text.component';
import { ActivatedRoute } from '@angular/router';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';

interface Message {
  text: string;
  sender: 'user' | 'agent';
  files?: File[];
  type?: 'text' | 'table' | 'option' | 'json';
  data?: any;
  formattedText?: string;
  figures?: ChatFigure[];
  rawJson?: string;
  copied?: boolean;
  loading?: boolean;
  completed?: boolean;
}

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    DynamicEditTableComponent,
    StreamingTextComponent,
  ],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @HostBinding('class.chat-standalone') isStandaloneChat = false;
  @Input() data: any;
  @ViewChild(DynamicEditTableComponent) private dynamicEditTable?: DynamicEditTableComponent;
  @ViewChild('chatBody') private chatBody!: ElementRef;

  messages: Message[] = [];
  currentMessage = '';
  isLoading = false;
  selectedFiles: File[] = [];
  isDarkTheme = false;

  title = 'Chat'; // Default title
  welcomeMessage = 'Hello! How can I help you today?';
  apiUrl = '';

  private subscriptions = new Subscription();
  private previousMessageCount = 0;
  private conversationId: string | null = null;
  private activeStreamMessageIndex: number | null = null;
  // Figures from the SSE `meta` event, held back until text streaming
  // finishes so images never render before/above the answer text.
  private pendingStreamFigures: ChatFigure[] | null = null;
  private parentCardBodyEl: HTMLElement | null = null;
  private parentHostContainerEl: HTMLElement | null = null;
  private parentCardBodyPaddingInline: string | null = null;
  private parentHostContainerDisplayInline: string | null = null;
  private parentHostContainerFlexDirectionInline: string | null = null;
  private parentHostContainerHeightInline: string | null = null;
  private parentHostContainerMinHeightInline: string | null = null;
  private objectUrlMap = new Map<File, string>();
  private suppressNextMessageAutoScroll = false;

  constructor(
    private chatApiService:ChatApiService,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private responsiveLayoutService: ResponsiveLayoutService,
    private hostRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.attachChatCardBodyClass();
    // Ensure DPR zoom compensation and CSS variables are set
    // (needed when chat is loaded directly via reload, bypassing DashboardComponent)
    this.responsiveLayoutService.init();

    const configPath = this.resolveConfigPath();
    if (configPath) {
      this.configService.getApi(configPath, undefined, undefined, 'get').subscribe({
        next: (config: any) => {
          if (config) {
            this.title = config.title || this.title;
            this.welcomeMessage = config.welcomeMessage || this.welcomeMessage;
            this.apiUrl = config.apiUrl || '';
            this.applyStyleConfig(config);

            // Initialize messages AFTER config is loaded to show correct welcome message
            if (this.messages.length === 0) {
              this.messages.push({ text: this.welcomeMessage, sender: 'agent' });
            }
          }
        },
        error: (err) => {
          console.error('Failed to load chat config:', err);
          // Fallback initialization
          if (this.messages.length === 0) {
            this.messages.push({ text: this.welcomeMessage, sender: 'agent' });
          }
        },
      });
    } else {
      // Fallback if no config file specified
      if (this.messages.length === 0) {
        this.messages.push({ text: this.welcomeMessage, sender: 'agent' });
      }
    }
  }

  private resolveConfigPath(): string {
    const widgetConfigPath =
      this.data?.chatConfigPath ??
      this.data?.configPath ??
      this.data?.config_path ??
      this.data?.configFile;

    const routeConfigPath = this.route.snapshot.data['configFile'];
    const rawPath = widgetConfigPath || routeConfigPath || 'assets/chat-config/chat-config.json';

    if (typeof rawPath !== 'string') {
      return 'assets/chat-config/chat-config.json';
    }

    const trimmedPath = rawPath.trim();
    if (!trimmedPath) {
      return 'assets/chat-config/chat-config.json';
    }

    if (trimmedPath.startsWith('assets/')) {
      return trimmedPath;
    }

    if (!trimmedPath.includes('/') && trimmedPath.endsWith('.json')) {
      return `assets/chat-config/${trimmedPath}`;
    }

    return `assets/${trimmedPath}`;
  }

  private applyStyleConfig(config: any): void {
    const styles = config?.styles || config?.chatStyle || config?.theme?.chat || {};
    const sizeStyleMap: Record<string, string | undefined> = {
      '--chat-footer-height': styles.footerHeight,
      '--chat-input-min-height': styles.inputMinHeight,
      '--chat-message-font-size': styles.messageFontSize,
      '--chat-agent-message-font-size': styles.agentMessageFontSize,
      '--chat-user-message-font-size': styles.userMessageFontSize,
      '--chat-message-padding-y': styles.messagePaddingY,
      '--chat-message-padding-x': styles.messagePaddingX,
    };

    for (const [cssVariable, value] of Object.entries(sizeStyleMap)) {
      const sanitizedValue = this.sanitizeCssSize(value);
      if (sanitizedValue) {
        this.renderer.setStyle(this.hostRef.nativeElement, cssVariable, sanitizedValue);
      }
    }

    const lineHeight = this.sanitizeCssNumber(styles.messageLineHeight);
    if (lineHeight) {
      this.renderer.setStyle(this.hostRef.nativeElement, '--chat-message-line-height', lineHeight);
    }
  }

  private sanitizeCssSize(value: unknown): string | null {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return `${value}px`;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    return /^\d+(\.\d+)?(px|rem|em)$/.test(trimmedValue) ? trimmedValue : null;
  }

  private sanitizeCssNumber(value: unknown): string | null {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return String(value);
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    return /^\d+(\.\d+)?$/.test(trimmedValue) ? trimmedValue : null;
  }

  ngAfterViewChecked() {
    if (this.messages.length !== this.previousMessageCount) {
      // Do not auto-scroll on the very first render (initial/welcome state).
      if (this.previousMessageCount === 0) {
        this.previousMessageCount = this.messages.length;
        return;
      }
      if (this.suppressNextMessageAutoScroll) {
        this.suppressNextMessageAutoScroll = false;
        this.previousMessageCount = this.messages.length;
        return;
      }
      this.scrollToBottom();
      this.previousMessageCount = this.messages.length;
    }
  }

  toggleTheme(): void {
    this.isDarkTheme = !this.isDarkTheme;

    if (this.isDarkTheme) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
  }

  scrollToBottom(): void {
    try {
      setTimeout(() => {
        const chatBodyEl = this.chatBody?.nativeElement;
        if (!chatBodyEl) {
          return;
        }

        // Auto-scroll only when content actually overflows the viewport.
        const hasOverflow = chatBodyEl.scrollHeight > chatBodyEl.clientHeight;
        if (!hasOverflow) {
          return;
        }

        chatBodyEl.scrollTop = chatBodyEl.scrollHeight;
      }, 0);
    } catch (err) {}
  }

  ngOnDestroy(): void {
    this.detachChatCardBodyClass();
    this.revokeAllPreviewUrls();
    this.subscriptions.unsubscribe();
  }

  private attachChatCardBodyClass(): void {
    this.parentHostContainerEl = this.hostRef.nativeElement.parentElement;
    if (this.parentHostContainerEl) {
      this.parentHostContainerDisplayInline = this.parentHostContainerEl.style.display || null;
      this.parentHostContainerFlexDirectionInline = this.parentHostContainerEl.style.flexDirection || null;
      this.parentHostContainerHeightInline = this.parentHostContainerEl.style.height || null;
      this.parentHostContainerMinHeightInline = this.parentHostContainerEl.style.minHeight || null;

      this.renderer.setStyle(this.parentHostContainerEl, 'display', 'flex');
      this.renderer.setStyle(this.parentHostContainerEl, 'flex-direction', 'column');
      this.renderer.setStyle(this.parentHostContainerEl, 'height', '100%');
      this.renderer.setStyle(this.parentHostContainerEl, 'min-height', '0');
    }

    this.parentCardBodyEl = this.hostRef.nativeElement.closest('.card-body') as HTMLElement | null;
    this.isStandaloneChat = !this.parentCardBodyEl;
    if (this.parentCardBodyEl) {
      this.renderer.addClass(this.parentCardBodyEl, 'dvm-chat-card-body--flush');
      this.parentCardBodyPaddingInline = this.parentCardBodyEl.style.padding || null;
      this.renderer.setStyle(this.parentCardBodyEl, 'padding', '0');
    }
  }

  private detachChatCardBodyClass(): void {
    this.isStandaloneChat = false;
    if (this.parentHostContainerEl) {
      if (this.parentHostContainerDisplayInline !== null) {
        this.renderer.setStyle(this.parentHostContainerEl, 'display', this.parentHostContainerDisplayInline);
      } else {
        this.renderer.removeStyle(this.parentHostContainerEl, 'display');
      }
      if (this.parentHostContainerFlexDirectionInline !== null) {
        this.renderer.setStyle(this.parentHostContainerEl, 'flex-direction', this.parentHostContainerFlexDirectionInline);
      } else {
        this.renderer.removeStyle(this.parentHostContainerEl, 'flex-direction');
      }
      if (this.parentHostContainerHeightInline !== null) {
        this.renderer.setStyle(this.parentHostContainerEl, 'height', this.parentHostContainerHeightInline);
      } else {
        this.renderer.removeStyle(this.parentHostContainerEl, 'height');
      }
      if (this.parentHostContainerMinHeightInline !== null) {
        this.renderer.setStyle(this.parentHostContainerEl, 'min-height', this.parentHostContainerMinHeightInline);
      } else {
        this.renderer.removeStyle(this.parentHostContainerEl, 'min-height');
      }

      this.parentHostContainerEl = null;
      this.parentHostContainerDisplayInline = null;
      this.parentHostContainerFlexDirectionInline = null;
      this.parentHostContainerHeightInline = null;
      this.parentHostContainerMinHeightInline = null;
    }

    if (this.parentCardBodyEl) {
      if (this.parentCardBodyPaddingInline !== null) {
        this.renderer.setStyle(this.parentCardBodyEl, 'padding', this.parentCardBodyPaddingInline);
      } else {
        this.renderer.removeStyle(this.parentCardBodyEl, 'padding');
      }
      this.renderer.removeClass(this.parentCardBodyEl, 'dvm-chat-card-body--flush');
      this.parentCardBodyEl = null;
      this.parentCardBodyPaddingInline = null;
    }
  }

  startNewSession(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.isLoading = false;
    this.messages = [];
    this.conversationId = null;
    this.activeStreamMessageIndex = null;
    this.previousMessageCount = 0;
    this.messages.push({ text: this.welcomeMessage, sender: 'agent' });
    this.scrollToBottom();
  }

  sendMessageOnEnter(event: Event | KeyboardEvent): void {
    const keyboardEvent = event as KeyboardEvent; // cast to KeyboardEvent
    if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Minimal, dependency-free Markdown → HTML converter for agent answers.
   *
   * Handles the subset the backend actually emits: ATX headings (#..######),
   * **bold**, *italic*, `inline code`, ordered lists (1. ), unordered lists
   * (- or * bullets, optionally indented), and paragraph breaks.
   *
   * Block structure is parsed line-by-line so heading markers and list
   * markers are stripped (the old single-pass regex left "###" / "1." in the
   * text and let the *italic* rule mangle "* " bullets). Inline formatting is
   * applied per text fragment, bold BEFORE italic so "**x**" isn't eaten by
   * the single-asterisk rule.
   *
   * Output uses only tags Angular's HTML sanitizer keeps (h1-h6, p, ul, ol,
   * li, strong, em, code, br). Figures are rendered separately from
   * msg.figures in the template, so this never touches images.
   */
  formatText(text: string): string {
    const escapeHtml = (s: string): string =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const inline = (s: string): string => {
      let r = escapeHtml(s);
      // inline code first so ** / * inside backticks are left alone
      r = r.replace(/`([^`]+)`/g, '<code>$1</code>');
      // bold before italic
      r = r.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
      r = r.replace(/__([^_]+?)__/g, '<strong>$1</strong>');
      // italic (single * or _) — bullets were already stripped at block level
      r = r.replace(/\*([^*\n]+?)\*/g, '<em>$1</em>');
      r = r.replace(/(^|[^_])_([^_\n]+?)_/g, '$1<em>$2</em>');
      return r;
    };

    const lines = (text || '').split(/\r?\n/);
    const html: string[] = [];
    // Stack of open lists. While a frame is on the stack its <li> is open;
    // we close it only when a sibling/closing line arrives. This keeps a
    // single continuous <ol> (1,2,3…) instead of restarting at 1 every time
    // an indented sub-bullet appears, and nests sub-bullets inside the item.
    const stack: Array<{ type: 'ul' | 'ol'; indent: number }> = [];
    let paragraph: string[] = [];

    const flushParagraph = () => {
      if (paragraph.length) {
        html.push(`<p>${paragraph.map(inline).join('<br>')}</p>`);
        paragraph = [];
      }
    };
    const closeAllLists = () => {
      while (stack.length) {
        const top = stack.pop()!;
        html.push(`</li></${top.type}>`);
      }
    };

    // --- GFM pipe-table helpers ---
    // Split a "| a | b |" row into trimmed cells; honours escaped \| .
    const splitCells = (s: string): string[] => {
      let t = s.trim();
      if (t.startsWith('|')) t = t.slice(1);
      if (t.endsWith('|')) t = t.slice(0, -1);
      return t.split(/(?<!\\)\|/).map((c) => c.replace(/\\\|/g, '|').trim());
    };
    // A delimiter row is the "| --- | :--: |" line under the header.
    const isDelimRow = (s: string): boolean => {
      const t = (s || '').trim();
      if (!t.includes('-') || !t.includes('|')) return false;
      const cells = splitCells(t);
      return (
        cells.length > 0 &&
        cells.every((c) => /^:?-{1,}:?$/.test(c.replace(/\s+/g, '')))
      );
    };

    for (let li = 0; li < lines.length; li++) {
      const raw = lines[li];
      // count leading spaces for nesting depth (tabs → 4 spaces)
      const indent = (raw.match(/^[ \t]*/)?.[0] || '')
        .replace(/\t/g, '    ').length;
      const line = raw.trim();

      // blank line → end paragraph (lists tolerate blank lines between items)
      if (!line) {
        flushParagraph();
        continue;
      }

      // GFM pipe table: a header row immediately followed by a delimiter
      // row ("| --- | --- |"). Rendered as a real <table> so tables in the
      // answer display as a proper table instead of raw pipe markdown.
      if (
        line.includes('|') &&
        li + 1 < lines.length &&
        isDelimRow(lines[li + 1])
      ) {
        const header = splitCells(line);
        const delim = splitCells(lines[li + 1].trim());
        if (header.length >= 1 && delim.length === header.length) {
          flushParagraph();
          closeAllLists();
          const aligns = delim.map((d) => {
            const t = d.replace(/\s+/g, '');
            const left = t.startsWith(':');
            const right = t.endsWith(':');
            return right && left ? 'center' : right ? 'right' : left ? 'left' : '';
          });
          const al = (i: number) => (aligns[i] ? ` align="${aligns[i]}"` : '');

          const body: string[][] = [];
          let k = li + 2;
          while (k < lines.length) {
            const bl = lines[k].trim();
            if (!bl || !bl.includes('|')) break;
            body.push(splitCells(bl));
            k++;
          }

          let tbl = '<table class="md-table"><thead><tr>';
          header.forEach((c, i) => (tbl += `<th${al(i)}>${inline(c)}</th>`));
          tbl += '</tr></thead>';
          if (body.length) {
            tbl += '<tbody>';
            for (const row of body) {
              tbl += '<tr>';
              for (let i = 0; i < header.length; i++) {
                tbl += `<td${al(i)}>${inline((row[i] ?? '').trim())}</td>`;
              }
              tbl += '</tr>';
            }
            tbl += '</tbody>';
          }
          tbl += '</table>';
          html.push(tbl);
          li = k - 1; // skip the consumed table lines
          continue;
        }
      }

      // heading: #..###### text
      const h = line.match(/^(#{1,6})\s+(.*)$/);
      if (h) {
        flushParagraph();
        closeAllLists();
        const level = h[1].length;
        html.push(`<h${level}>${inline(h[2].trim())}</h${level}>`);
        continue;
      }

      const ol = line.match(/^\d+[.)]\s+(.*)$/);
      const ul = ol ? null : line.match(/^[-*]\s+(.*)$/);

      if (ol || ul) {
        flushParagraph();
        const type: 'ul' | 'ol' = ol ? 'ol' : 'ul';
        const content = inline((ol ? ol[1] : ul![1]).trim());

        // close deeper / equal-indent-but-different lists
        while (
          stack.length &&
          (stack[stack.length - 1].indent > indent ||
            (stack[stack.length - 1].indent === indent &&
              stack[stack.length - 1].type !== type))
        ) {
          const top = stack.pop()!;
          html.push(`</li></${top.type}>`);
        }

        const cur = stack[stack.length - 1];
        if (cur && cur.indent === indent && cur.type === type) {
          // sibling item: close previous <li>, open a new one
          html.push(`</li><li>${content}`);
        } else {
          // open a new (possibly nested) list inside the current open <li>
          html.push(`<${type}><li>${content}`);
          stack.push({ type, indent });
        }
        continue;
      }

      // plain text → paragraph (ends any open lists)
      closeAllLists();
      paragraph.push(line);
    }

    flushParagraph();
    closeAllLists();
    return html.join('');
  }

  async sendMessage(): Promise<void> {
    if ((!this.currentMessage.trim() && this.selectedFiles.length === 0) || this.isLoading) {
      return;
    }

    if (this.dynamicEditTable && this.dynamicEditTable.isTableEditable()) {
      // Find and remove the last message containing the table
      const tableMessageIndex = this.messages.map((m) => m.type).lastIndexOf('table');
      if (tableMessageIndex > -1) {
        this.messages.splice(tableMessageIndex, 1);
      }
    }

    const jsonMessageIndex = this.messages.map((m) => m.type).lastIndexOf('json');
    if (jsonMessageIndex > -1) {
      this.messages.splice(jsonMessageIndex, 1);
    }

    const userMessage: Message = {
      text: this.currentMessage,
      sender: 'user',
      files: [...this.selectedFiles],
    };

    this.suppressNextMessageAutoScroll = true;
    this.messages.push(userMessage);
    const messageToApi = this.currentMessage.trim();
    this.currentMessage = '';
    this.selectedFiles = [];
    this.isLoading = true;

    const agentMessage: Message = {
      text: '',
      sender: 'agent',
      formattedText: '',
      figures: [],
      loading: true,
      completed: false,
    };
    this.messages.push(agentMessage);
    this.activeStreamMessageIndex = this.messages.length - 1;

    const streamSubscription = this.chatApiService
      .streamMessage(messageToApi, this.conversationId, this.apiUrl, true)
      .subscribe({
        next: (event: ChatStreamEvent) => this.handleStreamEvent(event),
        error: (error: any) => {
          this.isLoading = false;
          console.error('API Error:', error);
          const errorMessage = error?.message || 'Sorry, something went wrong.';
          this.applyStreamError(errorMessage);
        },
        complete: () => {
          this.isLoading = false;
          this.finalizeActiveStreamMessage();
        },
      });

    this.subscriptions.add(streamSubscription);
  }

  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (fileList) {
      for (let i = 0; i < fileList.length; i++) {
        this.selectedFiles.push(fileList[i]);
      }
    }
  }

  removeFile(fileToRemove: File): void {
    this.selectedFiles = this.selectedFiles.filter((file) => file !== fileToRemove);
  }

  isImageFile(file: File): boolean {
    return typeof file?.type === 'string' && file.type.startsWith('image/');
  }

  getFilePreviewUrl(file: File): string | null {
    if (!this.isImageFile(file)) {
      return null;
    }

    const cached = this.objectUrlMap.get(file);
    if (cached) {
      return cached;
    }

    const url = URL.createObjectURL(file);
    this.objectUrlMap.set(file, url);
    return url;
  }

  trackFigure(index: number, figure: ChatFigure): string {
    return figure.id || `${index}`;
  }

  hasVisibleContent(message: Message): boolean {
    return Boolean(
      message.type ||
      message.files?.length ||
      message.figures?.length ||
      message.formattedText?.trim() ||
      message.text?.trim(),
    );
  }

  get shouldShowTypingIndicator(): boolean {
    if (!this.isLoading) {
      return false;
    }

    const activeMessage = this.getActiveStreamMessage();
    return !activeMessage || !this.hasVisibleContent(activeMessage);
  }

  private handleStreamEvent(event: ChatStreamEvent): void {
    switch (event.kind) {
      case 'meta':
        this.conversationId = event.conversation_id || this.conversationId;
        // Figures arrive in `meta` (first event) but we DON'T render them yet
        // — stash them and attach only once text streaming has finished, so
        // images never appear above/before the answer text.
        if (event.figures?.length) {
          this.pendingStreamFigures = event.figures;
        }
        if (event.scope_message) {
          this.updateActiveStreamText(event.scope_message, true);
        }
        break;
      case 'token':
        this.updateActiveStreamText(event.text);
        break;
      case 'done':
        this.conversationId = event.conversation_id || this.conversationId;
        if (event.answer) {
          this.updateActiveStreamText(event.answer, true);
        }
        if (event.scope_message && !event.answer) {
          this.updateActiveStreamText(event.scope_message, true);
        }
        // Streaming finished — NOW render figures. Prefer figures from the
        // done event if the backend sends them; otherwise use the ones
        // stashed from the meta event.
        {
          const finalFigures = event.figures?.length
            ? event.figures
            : this.pendingStreamFigures;
          if (finalFigures?.length) {
            this.updateActiveStreamFigures(finalFigures);
          }
        }
        this.pendingStreamFigures = null;
        this.finalizeActiveStreamMessage();
        break;
      case 'error':
        this.applyStreamError(event.message);
        break;
    }
  }

  private updateActiveStreamText(text: string, replace = false): void {
    const message = this.getActiveStreamMessage();
    if (!message) {
      return;
    }

    message.text = replace ? text : `${message.text}${text}`;
    message.formattedText = this.formatText(message.text);
  }

  private updateActiveStreamFigures(figures: ChatFigure[]): void {
    const message = this.getActiveStreamMessage();
    if (!message) {
      return;
    }

    message.figures = figures
      .filter((figure) => figure?.url)
      .map((figure) => ({
        ...figure,
        url: this.chatApiService.resolveAssetUrl(figure.url, this.apiUrl),
      }));
  }

  private applyStreamError(messageText: string): void {
    this.pendingStreamFigures = null;
    const message = this.getActiveStreamMessage();
    if (message) {
      message.text = messageText;
      message.formattedText = this.formatText(messageText);
      message.loading = false;
      message.completed = true;
      this.activeStreamMessageIndex = null;
      return;
    }

    this.messages.push({
      text: messageText,
      sender: 'agent',
      formattedText: this.formatText(messageText),
    });
  }

  private finalizeActiveStreamMessage(): void {
    const message = this.getActiveStreamMessage();
    if (!message) {
      return;
    }

    message.loading = false;
    message.completed = true;
    message.formattedText = this.formatText(message.text);
    this.activeStreamMessageIndex = null;
  }

  private getActiveStreamMessage(): Message | null {
    if (this.activeStreamMessageIndex === null) {
      return null;
    }

    return this.messages[this.activeStreamMessageIndex] ?? null;
  }

  private revokeAllPreviewUrls(): void {
    this.objectUrlMap.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrlMap.clear();
  }

  onOptionSelected(option: string): void {
    this.currentMessage = option;
    this.sendMessage();

    // Find and remove the last message containing options
    const optionMessageIndex = this.messages.map((m) => m.type).lastIndexOf('option');
    if (optionMessageIndex > -1) {
      this.messages.splice(optionMessageIndex, 1);
    }
  }

  copyJson(msg: Message): void {
    if (msg.rawJson) {
      navigator.clipboard
        .writeText(msg.rawJson)
        .then(() => {
          msg.copied = true;
          setTimeout(() => {
            msg.copied = false;
          }, 2000); // Hide message after 2 seconds
        })
        .catch((err) => {
          console.error('Failed to copy JSON: ', err);
        });
    }
  }
}
